import React, { useState } from 'react';
import { Stack, Typography, IconButton, Box, TextField, Collapse } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useDispatch } from 'react-redux';

import { updateQty, removeItem, setLineDiscount, setLineNote } from '../store/cartSlice';

/**
 * A single line item in the cart.
 *
 * Layout:
 *   - Item name + rate x qty + amount
 *   - Qty controls (+/-) + expand toggle + delete
 *   - Note preview (shown inline when collapsed, if note exists)
 *   - Expandable section: discount % + note input
 */
export default function CartItem({ item, quotedItem }) {
  const dispatch = useDispatch();
  const [expanded, setExpanded] = useState(false);

  // Only force-expand the accordion for discount, not for notes
  const hasDiscount = item.discount_percentage > 0;
  const showAccordion = expanded || hasDiscount;
  const appliedRules = quotedItem?.pricing_rules || [];
  const serverAmountChanged = quotedItem && Math.abs((quotedItem.amount || 0) - item.amount) > 0.009;

  return (
    <Box sx={{ py: 1, px: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
      {/* Main row: name, rate x qty, amount */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Box sx={{ flexGrow: 1, minWidth: 0, mr: 1 }}>
          <Typography variant="body2" fontWeight={600} noWrap title={item.item_name}>
            {item.item_name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {item.rate.toFixed(2)} x {item.qty}
            {hasDiscount && (
              <span style={{ color: '#c62828' }}> -{item.discount_percentage}%</span>
            )}
          </Typography>
        </Box>
        <Typography variant="body2" fontWeight={700} sx={{ whiteSpace: 'nowrap' }}>
          {item.amount.toFixed(2)}
        </Typography>
      </Stack>

      {/* Qty controls row */}
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5 }}>
        <IconButton
          size="small"
          onClick={() => dispatch(updateQty({ item_code: item.item_code, qty: item.qty - 1 }))}
        >
          <RemoveIcon fontSize="small" />
        </IconButton>

        <Typography variant="body2" sx={{ minWidth: 24, textAlign: 'center' }}>
          {item.qty}
        </Typography>

        <IconButton
          size="small"
          onClick={() => dispatch(updateQty({ item_code: item.item_code, qty: item.qty + 1 }))}
        >
          <AddIcon fontSize="small" />
        </IconButton>

        <Box sx={{ flexGrow: 1 }} />

        <IconButton size="small" onClick={() => setExpanded(!expanded)}>
          {showAccordion ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>

        <IconButton
          size="small"
          color="error"
          onClick={() => dispatch(removeItem(item.item_code))}
        >
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Stack>

      {/* Note preview — shown below qty controls when accordion is collapsed */}
      {!expanded && item.note && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 0.5, fontStyle: 'italic', display: 'block' }}
        >
          {item.note}
        </Typography>
      )}

      {(appliedRules.length > 0 || serverAmountChanged) && (
        <Typography
          variant="caption"
          color="success.main"
          sx={{ mt: 0.5, display: 'block' }}
        >
          {appliedRules.length > 0
            ? `Promotion: ${appliedRules.join(', ')}`
            : `Server total: ${(quotedItem.amount || 0).toFixed(2)}`}
        </Typography>
      )}

      {/* Expandable: discount + note editing */}
      <Collapse in={showAccordion}>
        <Stack spacing={1} sx={{ mt: 1, mb: 0.5 }}>
          <TextField
            size="small"
            label="Discount %"
            type="number"
            value={item.discount_percentage || ''}
            onChange={(e) =>
              dispatch(
                setLineDiscount({
                  item_code: item.item_code,
                  discount_percentage: parseFloat(e.target.value) || 0,
                }),
              )
            }
            inputProps={{ min: 0, max: 100, step: 1 }}
            sx={{ width: 120 }}
          />
          <TextField
            size="small"
            label="Note"
            value={item.note || ''}
            onChange={(e) =>
              dispatch(setLineNote({ item_code: item.item_code, note: e.target.value }))
            }
            fullWidth
          />
        </Stack>
      </Collapse>
    </Box>
  );
}
